import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Cloudflare Turnstile for S7's `setContact` (contract: `turnstileToken`, verified by the API).
 *
 * With `VITE_TURNSTILE_SITE_KEY` set, S7 renders the managed widget (invisible unless
 * Cloudflare wants an interaction) and gets a token from it; the script is injected once per
 * page (the shell's widget for the guarded API calls, broker.ts, loads it too), never in tests. Locally the site key is Cloudflare's published test key
 * (`1x00000000000000000000AA`, always passes) and the API runs its fake verifier.
 *
 * Without a site key (tests, the fake API, a build that forgot it) there is no widget and the
 * token is a fixed string: the fake API and the API's fake verifier accept it; a production
 * API answers `422 turnstile_failed`, which S7 shows. `setTurnstileStubToken('turnstile-fail')`
 * makes the fake refuse it (and the MSW handlers refuse the guarded calls).
 */
export const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** How long a submit waits for the widget's token before sending without one. */
export const TOKEN_WAIT_MS = 10_000;

export interface TurnstileApi {
  render(el: HTMLElement, options: Record<string, unknown>): string | undefined;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/** Injects Cloudflare's script once per page and resolves with `window.turnstile`. */
export function loadTurnstileScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = TURNSTILE_SCRIPT;
    s.async = true;
    s.defer = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing')));
    s.onerror = () => {
      loading = null; // a later try may work (offline, a blocker)
      s.remove();
      reject(new Error('turnstile script failed to load'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

let stubToken = 'turnstile-stub-token';

/** Tests and the fake: the token handed out when there is no site key. */
export function setTurnstileStubToken(token: string) {
  stubToken = token;
}

/** The token without a site key (S7's hook and the guarded calls' broker). */
export function stubTurnstileToken() {
  return stubToken;
}

export interface UseTurnstileOptions {
  siteKey?: string;
  /** Tests inject a fake loader; the default injects Cloudflare's script. */
  load?: () => Promise<TurnstileApi>;
  /** Cloudflare's `action` (shows in its analytics): which form this is. */
  action?: string;
}

export interface Turnstile {
  /** The current token, or null while the widget works (or failed). */
  token: string | null;
  /** The token for a submit: waits for the widget up to TOKEN_WAIT_MS, else ''. */
  getToken: () => Promise<string>;
  /** A token is single-use: call after every submit, whatever the answer. */
  reset: () => void;
  /** The widget's container, for the form (null without a site key). */
  widget: ReactNode;
}

export function useTurnstile({
  siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY,
  load = loadTurnstileScript,
  action,
}: UseTurnstileOptions = {}): Turnstile {
  const box = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | undefined>(undefined);
  const api = useRef<TurnstileApi | null>(null);
  const [token, setToken] = useState<string | null>(siteKey ? null : stubToken);
  const tokenRef = useRef(token);
  const waiters = useRef<((t: string) => void)[]>([]);

  const settle = useCallback((t: string | null) => {
    tokenRef.current = t;
    setToken(t);
    if (t) waiters.current.splice(0).forEach((w) => w(t));
  }, []);

  useEffect(() => {
    if (!siteKey) return;
    let gone = false;
    load()
      .then((ts) => {
        if (gone || !box.current) return;
        api.current = ts;
        widgetId.current = ts.render(box.current, {
          sitekey: siteKey,
          action,
          appearance: 'interaction-only',
          size: 'flexible',
          'refresh-expired': 'auto',
          callback: (t: string) => settle(t),
          'expired-callback': () => settle(null),
          'error-callback': () => settle(null),
        });
      })
      .catch(() => {
        /* No script (offline, a blocker): the submit goes without a token and S7 shows the
           API's turnstile_failed. */
        waiters.current.splice(0).forEach((w) => w(''));
      });
    return () => {
      gone = true;
      if (widgetId.current !== undefined) api.current?.remove(widgetId.current);
      widgetId.current = undefined;
    };
  }, [siteKey, load, action, settle]);

  const getToken = useCallback((): Promise<string> => {
    if (!siteKey) return Promise.resolve(stubToken);
    if (tokenRef.current) return Promise.resolve(tokenRef.current);
    return new Promise<string>((resolve) => {
      const done = (t: string) => {
        clearTimeout(timer);
        resolve(t);
      };
      const timer = setTimeout(() => {
        waiters.current = waiters.current.filter((w) => w !== done);
        resolve('');
      }, TOKEN_WAIT_MS);
      waiters.current.push(done);
    });
  }, [siteKey]);

  const reset = useCallback(() => {
    if (!siteKey) return;
    settle(null);
    if (widgetId.current !== undefined) api.current?.reset(widgetId.current);
  }, [siteKey, settle]);

  return {
    token,
    getToken,
    reset,
    widget: siteKey ? <div ref={box} className="turnstile" data-testid="turnstile" /> : null,
  };
}
